import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { CalendarCheck, Info } from 'lucide-react';

import { CURRENCIES } from '../../../constants/currenciesConstants';
import defaultOrgAvatar from '../../../assets/images/CompanyDefaultLogo.svg';
import { normalizeURL } from '../../../helpers/commonHelper';

import styles from './TrackerDetail.module.scss';

/**
 * Shared status config — identical labels, class names, and colors to TrackerCard.
 * Uses className-based styling so card and detail pages stay in sync.
 */
const getStatusConfig = (t) => ({
  new: { label: t('tracker.statusNew', 'New'), className: 'statusNew' },
  applied: { label: t('tracker.applied', 'Applied'), className: 'statusApplied' },
  interview: { label: t('tracker.interviewing', 'Interviewing'), className: 'statusInterview' },
  offer: { label: t('tracker.statusOffer', 'Offer'), className: 'statusOffer' },
  hired: { label: `✓ ${t('tracker.statusHired', 'Hired')}`, className: 'statusHired' },
  hired_and_archived: { label: `✓ ${t('tracker.statusHired', 'Hired')}`, className: 'statusHired' },
  passed: { label: t('tracker.statusPassed', 'Passed'), className: 'statusPassed' },
  declined: { label: t('tracker.statusDeclined', 'Declined'), className: 'statusDeclined' },
  expired: { label: t('tracker.statusExpired', 'Expired'), className: 'statusExpired' },
  not_your_refer: { label: t('tracker.statusReferralDeclined', 'Referral Declined'), className: 'statusDeclined' },
  archived: { label: `✗ ${t('tracker.statusRejected', 'Rejected')}`, className: 'statusRejected' },
});

/** Format reward amount with correct currency symbol */
const formatReward = (rewards) => {
  if (!rewards?.monetary) return null;
  const symbol = CURRENCIES[rewards.currency_names] || '$';
  return `${symbol}${Number(rewards.monetary).toLocaleString()}`;
};

const PAYOUT_STATUS_LABELS = {
  open: 'Open',
  due: 'Due',
  past_due: 'Past Due',
  paid: 'Paid',
  tbd: 'TBD',
  ineligible: 'Ineligible',
};

const formatDueDate = (isoDate) => {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const PayoutItem = ({ payout, dueLabel, statusLabel, isIneligible, t }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className={styles.payoutItem}>
      <span className={styles.payoutItemReward}>{payout.reward_display}</span>
      <div className={styles.payoutItemRight}>
        {dueLabel && (
          <span className={styles.payoutItemDue}>
            <strong>{t('tracker.dueDate', 'Due Date')}:</strong> {dueLabel}
          </span>
        )}
        <span className={styles.payoutStatusRow}>
          <span className={classNames(styles.payoutItemStatus, styles[`payoutStatus_${payout.status}`])}>
            {statusLabel}
          </span>
          {isIneligible && (
            <span
              className={styles.tooltipWrapper}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <Info size={14} className={styles.infoIcon} />
              {showTooltip && <span className={styles.tooltip}>{payout.ineligible_reason}</span>}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

const JobInfoCard = ({ opportunity, direction, candidateStatus, onDecline, isDeclining, firstReferral }) => {
  const { t } = useTranslation('common');

  const statusConfig = useMemo(() => getStatusConfig(t), [t]);

  if (!opportunity) return null;

  const { title = '', organization_name: orgName = '', external_job_link: externalLink } = opportunity;
  const logoUrl = opportunity.logo_image_url || defaultOrgAvatar;
  const canDecline = candidateStatus === 'new' && direction === 'my';
  const showIneligible = direction === 'their' && firstReferral && !firstReferral.reward_eligible;
  const rewardDisplay = formatReward(opportunity.rewards);

  // Resolve interview stage label from interview_stages
  const resolveStageLabel = () => {
    if (candidateStatus?.includes('interview') || candidateStatus === 'new') {
      const stages = opportunity.interview_stages;
      if (stages && Array.isArray(stages)) {
        const match = stages.find((s) => s.value === candidateStatus);
        if (match) return { label: match.label, className: 'statusInterview' };
      }
    }
    return statusConfig[candidateStatus] || null;
  };

  const statusInfo = resolveStageLabel();

  return (
    <div className={styles.jobInfoCard}>
      {/* Ineligible badge — absolute top-right of the card */}
      {showIneligible && <span className={styles.ineligibleBadgeCorner}>{t('tracker.ineligible', 'Ineligible')}</span>}

      <div className={styles.jobCardHeader}>
        <img
          src={logoUrl}
          alt={orgName}
          className={styles.jobLogo}
          onError={(e) => {
            e.target.src = defaultOrgAvatar;
          }}
        />
        <div className={styles.jobHeaderText}>
          <h3 className={styles.jobTitle}>{title}</h3>
          <span className={styles.jobOrg}>{orgName}</span>
          {opportunity.location && (
            <span className={styles.jobLocation}>
              {opportunity.location.city}
              {opportunity.location.state ? `, ${opportunity.location.state}` : ''}
            </span>
          )}
        </div>
      </div>

      {/* Status pill — uses shared class names, consistent with TrackerCard */}
      {statusInfo && (
        <div className={styles.jobStatusRow}>
          <span className={classNames(styles.jobStatusPill, styles[statusInfo.className])}>{statusInfo.label}</span>
          {opportunity.work_type && <span className={styles.jobMeta}>{opportunity.work_type}</span>}
          {opportunity.work_place_type && <span className={styles.jobMeta}>{opportunity.work_place_type}</span>}
        </div>
      )}

      {/* Rewards (sent jobs) — hidden when actual payouts exist */}
      {direction === 'their' && rewardDisplay && !firstReferral?.payouts?.length && (
        <div className={styles.bountyRow}>
          <span className={styles.bountyLabel}>{t('tracker.rewards', 'Rewards')}</span>
          <span className={styles.bountyValue}>{rewardDisplay}</span>
        </div>
      )}

      {/* Payout due dates — visible for sent referrals with hired candidates */}
      {direction === 'their' && firstReferral?.payouts?.length > 0 && (
        <div className={styles.payoutsSection}>
          <h4 className={styles.payoutsSectionTitle}>
            <CalendarCheck size={16} />
            {t('tracker.rewardPayouts', 'Reward Payouts')}
          </h4>
          <div className={styles.payoutsList}>
            {firstReferral.payouts.map((payout) => {
              const dueLabel = formatDueDate(payout.due_date);
              const statusLabel = PAYOUT_STATUS_LABELS[payout.status] || payout.status;
              const isIneligible = payout.status === 'ineligible' && payout.ineligible_reason;
              return (
                <PayoutItem
                  key={payout.id}
                  payout={payout}
                  dueLabel={dueLabel}
                  statusLabel={statusLabel}
                  isIneligible={isIneligible}
                  t={t}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className={styles.actionRow}>
        {externalLink && candidateStatus !== 'archived' && (
          <a href={normalizeURL(externalLink)} target="_blank" rel="noopener noreferrer" className={styles.applyButton}>
            {t('tracker.viewJob', 'View Job')}
          </a>
        )}
        {canDecline && (
          <button type="button" className={styles.declineButton} onClick={onDecline} disabled={isDeclining}>
            {isDeclining ? t('tracker.declining', 'Declining...') : t('tracker.declineJob', 'Decline Job')}
          </button>
        )}
      </div>
    </div>
  );
};

export default JobInfoCard;
